import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule, NgbPaginationModule, NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { PerfectScrollbarService } from 'src/app/_services/perfect-scrollbar.service';
import { ChevronDownIconComponent } from '../../shared/components/icons';
import { PopupManager } from '../popup-manager';
import { ApiControllerService } from '../api-controller-service';
import { AlertService } from 'src/app/_services/alert.service';
import { DatetimePickerComponent, DateTimeSelection } from '../../shared/components/dialogs/datetime-picker/datetime-picker.component';
import { OtpValidationDialogComponent } from './otp-validation-dialog/otp-validation-dialog.component';
import { ExportService } from 'src/app/_services/export.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, GridOptions } from 'ag-grid-community';
import { GridSwitchRendererComponent } from 'src/app/shared/components/elements/switch-toggle/grid-switch-renderer.component';
import { HttpErrorResponse } from '@angular/common/http';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';
import { AuthService } from 'src/app/_services/auth.service';
import { SignUpStorageKey, Role } from 'src/app/_helpers/auth.constants';

interface ApiKey {
  id: string;
  clientId: string;
  label?: string;
  updateAt: string;
  updatedBy: string;
  rotateAt: string;
  status: ApiCredentialStatus;
}

export enum ApiCredentialStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}


interface ApiKeyCreatedResponse {
  clientId: string;
  clientSecret: string;
}

@Component({
  selector: 'app-api-credential',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbDropdownModule,
    ChevronDownIconComponent,
    DatetimePickerComponent,
    OtpValidationDialogComponent,
    AgGridAngular,
    NgbPaginationModule,
    GridSwitchRendererComponent
  ],
  templateUrl: './api-credential.component.html',
  styleUrls: ['./api-credential.component.css']
})
export class ApiCredentialComponent implements OnInit {
  copyedAPIKey = false;
  copyedAPISecret = false;
  constructor(
    private readonly apiControllerService: ApiControllerService,
    private readonly authService: AuthService,
    private readonly alertService: AlertService,
    private readonly perfectScrollbarService: PerfectScrollbarService,
    private readonly exportService: ExportService,
    private readonly calendar: NgbCalendar
  ) { }

  ngOnInit() {
    this.perfectScrollbarService.initializeScrollableList();
    this.columnDefs = this.columnDefs.map((c, i) => ({
      ...c,
      colId: (c as any).colId || c.headerName || `col_${i}`
    }));

    this.role = this.authService.getRole();
    if (this.role !== Role.PAY10_ADMIN) {
      this.fetchApiKeys();
    } else {
      // For admin: refresh overlay so pre-search shows no content
      this.refreshNoRowsOverlay();
    }
  }

  private readonly popupManager: PopupManager = new PopupManager();

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    this.popupManager.closeAll();
  }

  // AG Grid — admin without validated merchant (pre-search OR invalid): Find API Credentials; otherwise No API Keys Generated
  get noRowsTemplate(): string {
    if (this.role === Role.PAY10_ADMIN && !this.validatedMerchant) {
      return `
        <div class="no-results-overlay">
          <img src="assets/icons/Search-Glass.svg" alt="" class="no-results-icon" />
          <div class="no-results-title">Find API Credentials</div>
          <div class="no-results-description">Search with Merchant Name or MID to find API key configured for a merchant.</div>
        </div>
      `;
    }
    return `
      <div class="no-results-overlay">
        <img src="assets/icons/Glass-Lock.svg" alt="No key" class="no-results-icon" />
        <div class="no-results-title">No API Keys Generated</div>
        <div class="no-results-description">${this.role === Role.PAY10_ADMIN ? 'API Key for the merchant' : 'Generate an API key to start authenticating requests and integrating our APIs into your application.'}</div>
        <button class="generate-button-plus" id="empty-state-generate-btn">
          <span style="font-size: 20px; line-height: 1;">+</span>
          <span>Generate API Key</span>
        </button>
      </div>
    `;
  }

  private refreshNoRowsOverlay() {
    if (this.gridApi) {
      this.gridApi.setGridOption('overlayNoRowsTemplate', this.noRowsTemplate);
      if ((this.apiKeys?.length ?? 0) === 0) {
        this.gridApi.showNoRowsOverlay();
      }
    }
  }

  private gridApi!: GridApi<ApiKey>;
  onGridReady(params: GridReadyEvent<ApiKey>) {
    this.gridApi = params.api;
    // Only auto-fetch for non-admin; admin waits for merchant search
    if (this.role && this.role !== Role.PAY10_ADMIN) {
      this.fetchApiKeys();
    }
    this.refreshNoRowsOverlay();

    setTimeout(() => {
      this.perfectScrollbarService.initializeAgGrid();
      // Add event listener for the empty state generate button
      this.attachEmptyStateButtonListener();
    }, 100);
  }

  private attachEmptyStateButtonListener() {
    // Use setTimeout to ensure the DOM is updated
    setTimeout(() => {
      const emptyStateBtn = document.getElementById('empty-state-generate-btn');
      if (emptyStateBtn && !emptyStateBtn.dataset['listenerAttached']) {
        emptyStateBtn.dataset['listenerAttached'] = 'true';
        emptyStateBtn.addEventListener('click', () => {
          this.onGenerateApiKey();
        });
      }
    }, 100);
  }

  getRowId = (params: { data: ApiKey }) => String(params.data?.clientId);

  defaultColDef: ColDef = {
    filter: false,
    sortable: false,
    resizable: true,
    autoHeight: true,
    suppressHeaderMenuButton: true,
    flex: 1
  };

  columnDefs: ColDef[] = [
    { field: 'clientId', sortable: false, lockVisible: true, headerName: 'API Key', resizable: true,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.className = 'api-key-cell';
        container.innerHTML = `
          <span class="left-align-text-with-right-align-icon">${params.value}</span>
          <button class="button-without-border copy-icon-btn" type="button" aria-label="Copy API key">
            <img class="copy-icon-img" src="assets/icons/copy.svg" style="width: 20px; height: 20px;" alt="Copy"/>
          </button>
        `;
        const button = container.querySelector('.copy-icon-btn');
        const img = container.querySelector('.copy-icon-img') as HTMLImageElement;
        button?.addEventListener('click', () => {
          this.copyToClipboard(params.value);
          img.src = 'assets/icons/tick.svg';
          img.alt = 'Copied';
          setTimeout(() => {
            img.src = 'assets/icons/copy.svg';
            img.alt = 'Copy';
          }, 2000);
        });
        return container;
      }
    },
    { 
      field: 'label', 
      sortable: false, 
      lockVisible: true, 
      headerName: 'Label', 
      resizable: true,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.className = 'label-cell';
        
        const hasLabel = params.value && params.value.trim() !== '';
        
        if (hasLabel) {
          // Show label
          container.innerHTML = `
            <div class="label-display">
              <span class="label-text">${params.value}</span>
            </div>
          `;
        } else {
          // Show "Add Label" with plus icon
          container.innerHTML = `
            <div class="label-empty">
              <span class="label-empty-text left-align-text-with-right-align-icon label-placeholder">Add Label</span>
              <button class="button-without-border label-add-btn" type="button" aria-label="Add label">
                <img src="assets/icons/add.svg" style="width: 20px; height: 20px;" alt="Add" />
              </button>
            </div>
          `;
        }
        
        const button = container.querySelector('.label-edit-btn, .label-add-btn');
        button?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.onEditLabel(params, container);
        });
        
        return container;
      }
    },
    { field: 'updateAt', sortable: false, lockVisible: true, headerName: 'Updated Date', resizable: true },
    { field: 'updatedBy', sortable: false, lockVisible: true, headerName: 'Updated By', resizable: true },
    { field: 'rotateAt', sortable: false, lockVisible: true, headerName: 'Next Date', resizable: true },
    {
      field: 'status',
      sortable: false,
      lockVisible: true,
      headerName: 'Status',
      resizable: true,
      cellRenderer: GridSwitchRendererComponent,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' }
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      lockVisible: true,
      sortable: false,
      resizable: false,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.className = 'actions-cell';
        const isInactive = params.data.status === 'INACTIVE';
        const disabledClass = isInactive ? 'disabled' : '';
        container.innerHTML = `
            <button class="button-without-border actions-trigger ${disabledClass}" type="button" title="Reset Credential" aria-label="Rotate Key" ${isInactive ? 'disabled' : ''}>
              <img src="assets/icons/reset.svg" alt="Reset" />
            </button>
        `;
        const button = container.querySelector('.actions-trigger');
        if (!isInactive) {
          button?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.onRotateKey(params.data, e);
          });
        }
        return container;
      }
    }
  ];


  gridOptions: GridOptions = {
    context: {
      componentParent: this
    }
  };

  onStatusToggle(row: any, newValue: boolean): void {
    console.debug('row', row, 'newValue', newValue);
    row.status = newValue ?  ApiCredentialStatus.ACTIVE : ApiCredentialStatus.INACTIVE;
    this.apiControllerService.updateApiCredentialStatus(row.clientId, row.status, this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined).subscribe({
      next: () => {
        this.fetchApiKeys();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error updating apiCredential status:', error);
        this.alertService.customInfoPopup({
          icon: 'assets/img/failure.svg',
          title: 'Update Credential Failed',
          description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
          primaryButton: {
            text: 'OK'
          }}
        );
        this.fetchApiKeys();
      }
    })
    // Refresh the grid to update the actions button state
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  // Search
  searchQuery: string = '';

  // Banner
  showCredentialBanner: boolean = false;

  closeBanner() {
    this.showCredentialBanner = false;
    this.newAPIKey = undefined;
    this.newAPISecret = undefined;
  }

  // API Keys data
  apiKeys: ApiKey[] = [];
  role: string = '';
  Role = Role;
  hasSearched: boolean = false;
  merchantSearchQuery: string = '';
  isValidatingMerchant: boolean = false;
  merchantError: string = '';
  validatedMerchant: { merchantId: string; merchantName: string } | null = null;

  fetchApiKeys() {
    const merchantId = this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined;
    this.apiControllerService.getApiCredentials(merchantId).subscribe({
      next: (resp: any) => {
        const payload = resp?.response?.data || resp?.data || resp;
        this.apiKeys = Array.isArray(payload) ? payload : [];
        // Show error only for validated merchant with zero keys
        if (this.validatedMerchant && this.apiKeys.length === 0) {
          this.merchantError = `No result found for '${this.validatedMerchant.merchantName}'`;
        } else {
          this.merchantError = '';
        }
        if (this.gridApi) {
          this.gridApi.setGridOption('overlayNoRowsTemplate', this.noRowsTemplate);
          if (this.apiKeys.length === 0) {
            this.gridApi.showNoRowsOverlay();
            this.attachEmptyStateButtonListener();
          } else {
            this.gridApi.hideOverlay();
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        this.apiKeys = [];
        console.error('Error fetching apiCredentials:', error);
        if (this.gridApi) {
          this.gridApi.showNoRowsOverlay();
          this.attachEmptyStateButtonListener();
        }
        if(error.status !== 401) {
          this.alertService.customInfoPopup({
            icon: 'assets/img/failure.svg',
            title: 'API Credentials Fetching Failed',
            description: `${parseHttpErrorMessage(error)}. ${error.status ===403 ? '' : ' Please try again later.'}`,
            primaryButton: {
              text: 'OK'
            }}
          );
        }
      }
    });
  }

  applySearch() {
    const query = this.searchQuery.toLowerCase().trim();
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', query);
    }
  }

  // Dialog states
  isDateTimePickerVisible: boolean = false;
  isOtpDialogVisible: boolean = false;
  selectedApiKeyForRotation: ApiKey | null = null;
  selectedRotationDateTime: DateTimeSelection | null = null;
  dateTimePickerPosition: { top?: number; left?: number; bottom?: number; right?: number } | null = null;
  minValidDate: NgbDate | null = null;
  minValidTime: string | null = null;
  maxValidDate: NgbDate | null = null;
  maxValidTime: string | null = null;

  // Action handlers
  onToggleStatus(apiKey: ApiKey) {
    apiKey.status = apiKey.status === ApiCredentialStatus.ACTIVE ? ApiCredentialStatus.INACTIVE : ApiCredentialStatus.ACTIVE;
    this.apiControllerService.updateApiCredentialStatus(apiKey.clientId, apiKey.status, this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined).subscribe({
      next: () => {
        this.fetchApiKeys();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error updating apiCredential status:', error);
        this.alertService.customInfoPopup({
          icon: 'assets/img/failure.svg',
          title: 'Update Credential Failed',
          description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
          primaryButton: {
            text: 'OK'
          }}
        );
        this.fetchApiKeys();
      }
    });
  }

  onRotateKey(apiKey: ApiKey, event?: Event) {
    this.selectedApiKeyForRotation = apiKey;
    this.selectedRotationDateTime = null;
    
    // Set min valid date and time to current date and time
    const now = new Date();
    this.minValidDate = this.calendar.getToday();
    this.minValidTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // No max date - user can select any future date
    this.maxValidDate = null;
    this.maxValidTime = null;
    
    if (event) {
      // Get the button element - event.target might be the img inside the button
      const target = event.target as HTMLElement;
      const button = target.classList.contains('actions-trigger') 
        ? target 
        : target.closest('.actions-trigger');
      
      if (button) {
        const rect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        
        // Estimate picker height based on viewport width (responsive design)
        // Mobile (≤768px): slightly taller due to padding changes
        // Desktop: standard height
        const isMobile = viewportWidth <= 768;
        const pickerHeight = isMobile ? 510 : 490;
        const margin = 16;        
        // console.log('Button rect:', rect);

        // Check if there's enough space above the button
        const spaceAbove = rect.top;
        const hasEnoughSpaceAbove = spaceAbove >= (pickerHeight + margin);
        
        if (hasEnoughSpaceAbove) {
          // Position the picker's bottom-right corner at the button's top-left corner
          // This means the picker appears to the top-left of the button
          this.dateTimePickerPosition = { 
            bottom: viewportHeight - rect.top,
            right: viewportWidth - rect.left
          };
          // console.log('Positioning above button (bottom-right):', this.dateTimePickerPosition);
        } else {
          // Not enough space above, position below the button
          // Position the picker's top-right corner at the button's bottom-left corner
          this.dateTimePickerPosition = { 
            top: rect.bottom,
            right: viewportWidth - rect.left
          };
          // console.log('Positioning below button (top-right):', this.dateTimePickerPosition);
        }
      }
    }
    this.isDateTimePickerVisible = true;
  }

  handleDateTimeApply(dateTime: DateTimeSelection) {
    this.selectedRotationDateTime = dateTime;
    this.isDateTimePickerVisible = false;
    this.authService.resendOTP('ROTATE_CREDENTIAL').subscribe({
      next: (response: any) => {
        this.isOtpDialogVisible = true;
        const token = response?.response?.data?.token ?? response?.data?.token ?? response?.response?.token ?? response?.token ?? response?.response?.data?.otpToken;
        if (token) {
            localStorage.setItem(SignUpStorageKey.OTP_TOKEN, token);
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error sending OTP:', error);
      }
    });
  }

  handleOtpSubmit(otp: string) {
    this.isOtpDialogVisible = false;
    // Validate OTP and rotate key
    const isoUtcString: string = this.selectedRotationDateTime?.dateTime.toISOString() ?? '';
    console.debug('DateTime to rotate: ', this.selectedRotationDateTime?.dateTime,', isoUtcString: ', isoUtcString);
    this.authService.verifyOTP(otp, 'ROTATE_CREDENTIAL').subscribe({
      next: (response: any) => {
        if(this.selectedApiKeyForRotation?.clientId) {
          this.apiControllerService.rotateApiCredential(this.selectedApiKeyForRotation?.clientId, isoUtcString, this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined)
          .subscribe({
            next: (response: any) => {
              this.showCredentialBanner = true;
              this.newAPIKey = response.clientId;
              this.newAPISecret = response.clientSecret;
              this.alertService.customInfoPopup({
                icon: 'assets/img/Tick.svg',
                title: 'Credential Reset Successfully',
                description: `Your credential have been securely reset.`,
                primaryButton: {
                  text: 'Got it',
                  action: () => {
                    this.fetchApiKeys();
                  }
                }
              });
              // Reset states
              this.selectedApiKeyForRotation = null;
              this.selectedRotationDateTime = null;
            },
            error: (error: HttpErrorResponse) => {
              console.error('Error rotating API key:', error);
              // Reset states
              this.selectedApiKeyForRotation = null;
              this.selectedRotationDateTime = null;
              this.alertService.customInfoPopup({
                icon: 'assets/img/failure.svg',
                title: 'Rotate Credential Failed',
                description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
                primaryButton: {
                  text: 'OK'
                }}
              );
            }
          });
      }},
      error: (error: HttpErrorResponse) => {
        console.error('Error verifying OTP:', error);
        // Reset states
        this.selectedApiKeyForRotation = null;
        this.selectedRotationDateTime = null;
      }
    });
  }

  handleDateTimeDialogClose() {
    this.isDateTimePickerVisible = false;
    // Do not clear the selected API key if we just applied a datetime.
    // When Apply is clicked, the child component emits `apply` first (where we set
    // `selectedRotationDateTime`) and then emits `closed`. Without this guard,
    // we would null out `selectedApiKeyForRotation` before OTP submit, causing
    // it to be undefined in `handleOtpSubmit`.
    // if (!this.selectedRotationDateTime) {
    //   this.selectedApiKeyForRotation = null;
    // }
  }

  handleOtpDialogClose() {
    this.isOtpDialogVisible = false;
  }

  newAPIKey: string | undefined = undefined;
  newAPISecret: string | undefined = undefined;
  // Generate new API key
  onGenerateApiKey() {
    const merchantId = this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined;
    if (this.role === Role.PAY10_ADMIN && !merchantId) {
      this.alertService.customInfoPopup({
        icon: 'assets/img/Info.svg',
        title: 'Merchant Required',
        description: 'Please search and select a merchant first.',
        primaryButton: { text: 'OK' }
      });
      return;
    }
    this.apiControllerService.createApiCredential(merchantId).subscribe({
      next: (response: any) => {
        this.newAPIKey = response.clientId;
        this.newAPISecret = response.clientSecret;
        this.showCredentialBanner = true;
        this.fetchApiKeys();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error creating apiCredential:', error);
        let errorMessage: string = parseHttpErrorMessage(error);
        if(errorMessage?.includes('Exceeded maximum number of API credentials')) {
          errorMessage = 'Exceeded maximum number of API credentials';
        }
        this.alertService.customInfoPopup({
          icon: 'assets/img/failure.svg',
          title: 'Credential Generated Failed',
          description: errorMessage,
          primaryButton: {
            text: 'OK'
          }}
        );
        this.fetchApiKeys();
      }
    })
  }

  // Copy to clipboard
  copyToClipboard(keyword: string) {
    let text: string = '';
    if(keyword === 'newAPIKey') {
      text = this.newAPIKey ?? '';
    } else if(keyword === 'newAPISecret') {
      text = this.newAPISecret ?? '';
    } else {
      text = keyword;
    }
    navigator.clipboard.writeText(text).then(() => {
      if(keyword === 'newAPIKey') {
        this.copyedAPIKey = true;
        setTimeout(() => { this.copyedAPIKey = false; }, 2000);
      } else if(keyword === 'newAPISecret') {
        this.copyedAPISecret = true;
        setTimeout(() => { this.copyedAPISecret = false; }, 2000);
      }
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  }

  // Download API Key
  downloadApiKey() {
    const content = `API Key
${this.newAPIKey}
API Secret
${this.newAPISecret}`;
    
    this.exportService.downloadFile(content, 'API Credential.txt', 'text/plain').then(() => {
    }).catch(err => {
      console.error('Failed to download API credentials:', err);
    });
  }

  // Edit Label
  onEditLabel(params: any, container: HTMLElement) {
    const currentLabel = params.value || '';
    
    // Create input field
    container.innerHTML = `
      <div class="label-edit-mode">
        <input 
          type="text" 
          class="label-input" 
          value="${currentLabel}" 
          placeholder="Enter label"
          maxlength="50"
        />
        <button class="button-without-border label-save-btn" type="button" aria-label="Save label">
          <img src="assets/icons/tick.svg" alt="Save" />
        </button>
      </div>
    `;
    
    const input = container.querySelector('.label-input') as HTMLInputElement;
    const saveBtn = container.querySelector('.label-save-btn');
    
    // Focus the input
    setTimeout(() => input?.focus(), 0);
    
    const saveLabel = () => {
      const newLabel = input.value.trim();
      params.node.setDataValue('label', newLabel);
      
      // Call API to save label
      this.apiControllerService.updateApiCredentialLabel(params.data.clientId, newLabel, this.role === Role.PAY10_ADMIN ? this.validatedMerchant?.merchantId : undefined).subscribe({
        next: (response: any) => {
          this.fetchApiKeys();
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error updating label:', error);
          this.alertService.customInfoPopup({
            icon: 'assets/img/failure.svg',
            title: 'Update Credential Label Failed',
            description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
            primaryButton: {
              text: 'OK'
            }}
          );
        }
      });
      
      // Refresh the cell to show the new label
      if (this.gridApi) {
        this.gridApi.refreshCells({ rowNodes: [params.node], force: true });
      }
    };
    
    saveBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      saveLabel();
    });
    
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveLabel();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Refresh to cancel edit
        if (this.gridApi) {
          this.gridApi.refreshCells({ rowNodes: [params.node], force: true });
        }
      }
    });
    
    input?.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  onAdminSearchChange() {
    // No-op: search is click-only
  }

  clearMerchantSearch() {
    this.merchantSearchQuery = '';
    this.hasSearched = false;
    this.merchantError = '';
    this.validatedMerchant = null;
    this.apiKeys = [];
    this.refreshNoRowsOverlay();
  }

  searchMerchant() {
    const query = (this.merchantSearchQuery || '').trim();
    if (!query || this.isValidatingMerchant) return;

    this.isValidatingMerchant = true;
    this.merchantError = '';
    this.validatedMerchant = null;
    this.hasSearched = true;
    this.apiKeys = [];
    this.refreshNoRowsOverlay();

    this.apiControllerService.findMerchant(query).subscribe({
      next: (resp: any) => {
        this.isValidatingMerchant = false;
        const data = resp?.response?.data ?? resp?.data ?? null;
        if (data && data.merchantId) {
          this.validatedMerchant = {
            merchantId: data.merchantId,
            merchantName: data.merchantName
          };
          this.fetchApiKeys();
        } else {
          this.validatedMerchant = null;
          this.merchantError = `No result found for '${query}'`;
          this.refreshNoRowsOverlay();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isValidatingMerchant = false;
        this.validatedMerchant = null;
        console.error('Error finding merchant:', error);
        this.merchantError = `No result found for '${query}'`;
        this.refreshNoRowsOverlay();
      }
    });
  }
}
